import { useState, useEffect } from 'react';

const CLIENTS_KEY = 'LathiNyx_clients_v2';
const PROJECTS_KEY = 'LathiNyx_projects_v1';

function loadClients() {
  try { return JSON.parse(localStorage.getItem(CLIENTS_KEY)) || []; } catch { return []; }
}
function loadProjects() {
  try { return JSON.parse(localStorage.getItem(PROJECTS_KEY)) || []; } catch { return []; }
}
function saveProjects(data) {
  localStorage.setItem(PROJECTS_KEY, JSON.stringify(data));
}
function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2); }

const SVC_MAP = {
  webdev: { label: 'Web Dev', tagClass: 'tw' },
  ai: { label: 'AI', tagClass: 'ta' },
  research: { label: 'Research', tagClass: 'tr-tag' },
  other: { label: 'Other', tagClass: 'tw' },
};

// Merge client sub-projects into the global projects list (read-only from clients)
function getClientProjects(clients) {
  const result = [];
  clients.forEach(c => {
    (c.projects || []).forEach(p => {
      result.push({
        id: 'cp_' + c.id + '_' + p.id,
        name: p.title || p.name || '—',
        client: c.name,
        clientId: c.clientId || '',
        svc: c.svc === 'Web Dev' ? 'webdev' : c.svc === 'AI Training' ? 'ai' : c.svc === 'Research' ? 'research' : 'other',
        svcLabel: c.svc || 'Other',
        tagClass: c.svc === 'Web Dev' ? 'tw' : c.svc === 'AI Training' ? 'ta' : 'tr-tag',
        deadline: p.end || p.deadline || 'TBD',
        value: p.value ? '₹' + parseInt(p.value).toLocaleString('en-IN') : '—',
        prog: p.status === 'Completed' ? 100 : p.status === 'In Progress' ? 50 : 0,
        progColor: '#2563eb',
        invClass: 's-draft',
        invLabel: 'Not set',
        status: p.status === 'Completed' ? 'done' : p.status === 'In Progress' ? 'active' : 'pending',
        stClass: p.status === 'Completed' ? 's-done' : p.status === 'In Progress' ? 's-active' : 's-pending',
        stLabel: p.status || 'Pending',
        source: 'client',
      });
    });
  });
  return result;
}

export default function Projects() {
  const [ownProjects, setOwnProjects] = useState(() => loadProjects());
  const [clients, setClients] = useState(() => loadClients());
  const [filter, setFilter] = useState('all');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', clientId: '', svc: 'webdev', deadline: '', value: '', status: 'active' });

  // Reload clients from storage when component mounts (in case clients were updated)
  useEffect(() => {
    const refresh = () => setClients(loadClients());
    window.addEventListener('storage', refresh);
    return () => window.removeEventListener('storage', refresh);
  }, []);

  useEffect(() => { saveProjects(ownProjects); }, [ownProjects]);

  const clientProjects = getClientProjects(clients);
  const allProjects = [...clientProjects, ...ownProjects];

  const filtered = allProjects.filter(p => {
    if (filter === 'all') return true;
    if (['active', 'done', 'pending'].includes(filter)) return p.status === filter;
    return p.svc === filter;
  });

  const counts = {
    active: allProjects.filter(p => p.status === 'active').length,
    done: allProjects.filter(p => p.status === 'done').length,
    pending: allProjects.filter(p => p.status === 'pending').length,
  };

  const addProject = () => {
    if (!form.name) { alert('Please enter a project name'); return; }
    const selectedClient = clients.find(c => c.id === form.clientId);
    const s = SVC_MAP[form.svc] || SVC_MAP.other;
    const newProject = {
      id: uid(),
      name: form.name,
      client: selectedClient ? selectedClient.name : '—',
      clientId: selectedClient ? selectedClient.clientId : '',
      svc: form.svc,
      svcLabel: s.label,
      tagClass: s.tagClass,
      deadline: form.deadline || 'TBD',
      value: form.value ? '₹' + parseInt(form.value).toLocaleString('en-IN') : '—',
      prog: 0,
      progColor: '#2563eb',
      invClass: 's-draft',
      invLabel: 'Not set',
      status: form.status,
      stClass: form.status === 'active' ? 's-active' : 's-pending',
      stLabel: form.status === 'active' ? 'Active' : 'Pending',
      source: 'manual',
    };
    setOwnProjects(prev => [newProject, ...prev]);
    setShowAdd(false);
    setForm({ name: '', clientId: '', svc: 'webdev', deadline: '', value: '', status: 'active' });
  };

  const removeProject = (id) => {
    setOwnProjects(prev => prev.filter(p => p.id !== id));
  };

  const updateProjectStatus = (id, newStatus) => {
    const stMap = { active: { stClass: 's-active', stLabel: 'Active', prog: 30 }, done: { stClass: 's-done', stLabel: 'Done', prog: 100 }, pending: { stClass: 's-pending', stLabel: 'Pending', prog: 0 } };
    const s = stMap[newStatus] || stMap.pending;
    setOwnProjects(prev => prev.map(p => p.id === id ? { ...p, status: newStatus, ...s } : p));
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div className="d-proj-filter">
          {[['all','All'],['active','Active'],['done','Done'],['pending','Pending'],['webdev','Web Dev'],['ai','AI'],['research','Research']].map(([val, label]) => (
            <button key={val} className={`d-proj-filter-btn${filter === val ? ' active' : ''}`} onClick={() => setFilter(val)}>{label}</button>
          ))}
        </div>
        <button className="d-btn d-btn-primary" onClick={() => setShowAdd(true)}>+ New Project</button>
      </div>

      <div className="d-metrics d-metrics-3" style={{ marginBottom: 16 }}>
        <div className="d-mcard blue"><div className="d-mlabel">Active</div><div className="d-mval">{counts.active}</div></div>
        <div className="d-mcard green"><div className="d-mlabel">Completed</div><div className="d-mval">{counts.done}</div></div>
        <div className="d-mcard yellow"><div className="d-mlabel">Pending</div><div className="d-mval">{counts.pending}</div></div>
      </div>

      <div className="d-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="d-table">
          <thead>
            <tr>
              <th>Project</th><th>Client</th><th>ID</th><th>Service</th>
              <th>Deadline</th><th>Value</th><th>Progress</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} style={{ textAlign: 'center', color: '#9ca3af', padding: 32 }}>No projects yet. Add clients with projects or create one manually.</td></tr>
            )}
            {filtered.map(p => (
              <tr key={p.id || p.name}>
                <td><span style={{ fontWeight: 600, color: '#111827' }}>{p.name}</span>
                  {p.source === 'client' && <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 6 }}>via client</span>}
                </td>
                <td style={{ color: '#374151' }}>{p.client}</td>
                <td style={{ fontSize: 11, color: '#9ca3af' }}>{p.clientId || '—'}</td>
                <td><span className={`d-tag ${p.tagClass}`}>{p.svcLabel}</span></td>
                <td style={{ color: '#374151' }}>{p.deadline}</td>
                <td style={{ fontWeight: 600 }}>{p.value}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div className="d-prog-wrap"><div className="d-prog" style={{ width: p.prog + '%', background: p.progColor }} /></div>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>{p.prog}%</span>
                  </div>
                </td>
                <td>
                  {p.source !== 'client'
                    ? <select value={p.status} onChange={e => updateProjectStatus(p.id, e.target.value)}
                        style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, border: '1px solid #e5e7eb', background: p.status === 'done' ? '#f0fdf4' : p.status === 'active' ? '#eff6ff' : '#fffbeb', color: p.status === 'done' ? '#16a34a' : p.status === 'active' ? '#2563eb' : '#d97706', fontWeight: 600, cursor: 'pointer' }}>
                        <option value="active">Active</option>
                        <option value="done">Done</option>
                        <option value="pending">Pending</option>
                      </select>
                    : <span className={`d-st ${p.stClass}`}>{p.stLabel}</span>
                  }
                </td>
                <td>
                  {p.source !== 'client' && (
                    <button className="d-btn d-btn-sm" style={{ color: '#e11d48', borderColor: '#fecdd3', padding: '3px 8px' }} onClick={() => removeProject(p.id)}>✕</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="d-modal-overlay open" onClick={e => e.target === e.currentTarget && setShowAdd(false)}>
          <div className="d-modal">
            <div className="d-modal-title">New Project <span className="d-close-btn" onClick={() => setShowAdd(false)}>&times;</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="d-fg" style={{ gridColumn: '1/-1' }}>
                <div className="d-flabel">Project Name</div>
                <input className="d-finput" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="e.g. E-commerce Website" />
              </div>
              <div className="d-fg">
                <div className="d-flabel">Client</div>
                <select className="d-finput" value={form.clientId} onChange={e => setForm(p => ({...p, clientId: e.target.value}))}>
                  <option value="">— Select client —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}{c.company ? ` (${c.company})` : ''}</option>)}
                </select>
              </div>
              <div className="d-fg">
                <div className="d-flabel">Service</div>
                <select className="d-finput" value={form.svc} onChange={e => setForm(p => ({...p, svc: e.target.value}))}>
                  <option value="webdev">Web Dev</option>
                  <option value="ai">AI & ML</option>
                  <option value="research">Research</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="d-fg">
                <div className="d-flabel">Deadline</div>
                <input className="d-finput" type="date" value={form.deadline} onChange={e => setForm(p => ({...p, deadline: e.target.value}))} />
              </div>
              <div className="d-fg">
                <div className="d-flabel">Value (₹)</div>
                <input className="d-finput" type="number" value={form.value} onChange={e => setForm(p => ({...p, value: e.target.value}))} placeholder="45000" />
              </div>
              <div className="d-fg">
                <div className="d-flabel">Status</div>
                <select className="d-finput" value={form.status} onChange={e => setForm(p => ({...p, status: e.target.value}))}>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button className="d-btn" style={{ flex: 1 }} onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="d-btn d-btn-primary" style={{ flex: 1 }} onClick={addProject}>Add Project</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
